import React, { useState, useCallback } from "react";
import Cropper from "react-easy-crop";
import { X } from "lucide-react";

const createImage = (url) =>
    new Promise((resolve, reject) => {
        const image = new Image();
        image.addEventListener("load", () => resolve(image));
        image.addEventListener("error", (e) => reject(e));
        image.setAttribute("crossOrigin", "anonymous");
        image.src = url;
    });

async function getCroppedBlob(src, pixelCrop, format = "image/jpeg") {
    const image = await createImage(src);
    const canvas = document.createElement("canvas");
    canvas.width = Math.round(pixelCrop.width);
    canvas.height = Math.round(pixelCrop.height);
    const ctx = canvas.getContext("2d");
    ctx.drawImage(
        image,
        pixelCrop.x, pixelCrop.y, pixelCrop.width, pixelCrop.height,
        0, 0, pixelCrop.width, pixelCrop.height
    );
    return new Promise((resolve) => canvas.toBlob((b) => resolve(b), format, 0.92));
}

const PRESETS = [["4:5", 4 / 5], ["1:1", 1], ["3:2", 3 / 2], ["16:9", 16 / 9]];

export default function ImageCropModal({ open, src, format = "image/jpeg", initialAspect = 4 / 5, onCancel, onSave }) {
    const [crop, setCrop] = useState({ x: 0, y: 0 });
    const [zoom, setZoom] = useState(1);
    const [aspect, setAspect] = useState(initialAspect);
    const [areaPixels, setAreaPixels] = useState(null);
    const [busy, setBusy] = useState(false);
    const onComplete = useCallback((_, px) => setAreaPixels(px), []);

    if (!open) return null;

    const save = async () => {
        if (!areaPixels) return;
        setBusy(true);
        try {
            const blob = await getCroppedBlob(src, areaPixels, format);
            await onSave(blob);
        } catch (e) {
            setBusy(false);
            throw e;
        }
    };

    return (
        <div className="fixed inset-0 z-[90] flex items-center justify-center p-4" data-testid="crop-modal">
            <div className="absolute inset-0 bg-espresso/70 backdrop-blur-sm" onClick={() => !busy && onCancel()} />
            <div className="relative w-full max-w-lg bg-white shadow-2xl flex flex-col rounded-lg overflow-hidden">
                <div className="px-5 py-4 border-b border-[#e8e1d4] flex items-center justify-between">
                    <h3 className="font-display font-semibold text-lg">Adjust &amp; crop photo</h3>
                    <button onClick={onCancel} disabled={busy} data-testid="crop-close"><X className="h-5 w-5" /></button>
                </div>
                <div className="relative w-full bg-black" style={{ height: 340 }}>
                    <Cropper
                        image={src}
                        crop={crop}
                        zoom={zoom}
                        aspect={aspect}
                        onCropChange={setCrop}
                        onZoomChange={setZoom}
                        onCropComplete={onComplete}
                        restrictPosition={false}
                        objectFit="contain"
                    />
                </div>
                <div className="p-5 space-y-4">
                    <div>
                        <p className="text-[11px] uppercase tracking-widest text-neutral-500 mb-2">Aspect ratio</p>
                        <div className="flex flex-wrap gap-2">
                            {PRESETS.map(([lbl, val]) => (
                                <button key={lbl} onClick={() => setAspect(val)} data-testid={`crop-aspect-${lbl}`}
                                    className={`px-3 py-1.5 text-xs border transition-colors ${aspect === val ? "border-ink bg-ink text-white" : "border-[#d9cfbc] text-neutral-600 hover:border-ink"}`}>{lbl}</button>
                            ))}
                        </div>
                    </div>
                    <div>
                        <p className="text-[11px] uppercase tracking-widest text-neutral-500 mb-2">Zoom</p>
                        <input type="range" min={1} max={3} step={0.01} value={zoom} onChange={(e) => setZoom(Number(e.target.value))} data-testid="crop-zoom" className="w-full accent-brand-gold" />
                    </div>
                </div>
                <div className="px-5 py-4 border-t border-[#e8e1d4] flex gap-3">
                    <button onClick={save} disabled={busy} className="btn-primary flex-1" data-testid="crop-save">{busy ? "Saving…" : "Save photo"}</button>
                    <button onClick={onCancel} disabled={busy} className="btn-outline">Cancel</button>
                </div>
            </div>
        </div>
    );
}
